"use client";
import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchBusiness, updateBusiness, reset } from "@/state/business/business.slice";
import { useToast } from "@/components/ui/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Loader2, Building2, Save, Printer, Monitor, AlertTriangle } from "lucide-react";

export default function BusinessSettingsPage() {
    const dispatch = useDispatch();
    const { toast } = useToast();
    const { business, isLoading, error, message, success } = useSelector(
        (state) => ({
            business: state.business?.business,
            isLoading: state.business?.isLoading,
            error: state.business?.error,
            message: state.business?.message,
            success: state.business?.success,
        })
    );

    const [formData, setFormData] = useState({
        name: "",
        address: {
            street: "",
            city: "",
            state: "",
            zip: "",
            country: "",
        },
        contact: {
            phone: "",
            email: "",
            website: "",
        },
        tax_rate: 0,
        currency: "GHS",
        logo_url: "",
        receipt_notes: "Thank you for your business!",
        printer_type: "normal",
        allow_negative_stock: false,
        imagekit_public_key: "",
        imagekit_private_key: "",
        imagekit_url_endpoint: "",
    });

    useEffect(() => {
        dispatch(fetchBusiness());
    }, [dispatch]);

    useEffect(() => {
        if (business) {
            setFormData({
                name: business.name || "",
                address: {
                    street: business.address?.street || "",
                    city: business.address?.city || "",
                    state: business.address?.state || "",
                    zip: business.address?.zip || "",
                    country: business.address?.country || "",
                },
                contact: {
                    phone: business.contact?.phone || "",
                    email: business.contact?.email || "",
                    website: business.contact?.website || "",
                },
                tax_rate: business.tax_rate || 0,
                currency: business.currency || "GHS",
                logo_url: business.logo_url || "",
                receipt_notes: business.receipt_notes || "Thank you for your business!",
                printer_type: business.printer_type || "normal",
                allow_negative_stock: business.allow_negative_stock || false,
                imagekit_public_key: business.imagekit_public_key || "",
                imagekit_private_key: business.imagekit_private_key || "",
                imagekit_url_endpoint: business.imagekit_url_endpoint || "",
            });
        }
    }, [business]);

    useEffect(() => {
        if (error) {
            toast({
                variant: "destructive",
                title: "Error",
                description: typeof error === "string" ? error : error?.message || "An error occurred",
            });
            dispatch(reset());
        }
        if (success && message) {
            toast({
                title: "Success",
                description: message,
            });
            dispatch(reset());
        }
    }, [error, success, message, toast, dispatch]);

    const handleChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const handleNestedChange = (parent, field, value) => {
        setFormData((prev) => ({
            ...prev,
            [parent]: { ...prev[parent], [field]: value },
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        dispatch(updateBusiness(formData));
    };

    if (isLoading && !business) {
        return (
            <div className="flex justify-center items-center h-64">
                <Loader2 className="h-8 w-8 animate-spin" />
            </div>
        );
    }

    return (
        <div className="mx-auto py-4 space-y-6">
            <div className="flex items-center gap-3">
                <Building2 className="h-8 w-8 text-primary" />
                <div>
                    <h1 className="text-3xl font-bold">Business Settings</h1>
                    <p className="text-muted-foreground">Manage your business details for receipts and invoices</p>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
                {/* Business Info */}
                <Card>
                    <CardHeader>
                        <CardTitle>Business Information</CardTitle>
                        <CardDescription>Basic details about your business</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="grid gap-4 md:grid-cols-2">
                            <div className="space-y-2">
                                <Label htmlFor="name">Business Name</Label>
                                <Input
                                    id="name"
                                    value={formData.name}
                                    onChange={(e) => handleChange("name", e.target.value)}
                                    placeholder="Your Business Name"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="logo_url">Logo URL</Label>
                                <Input
                                    id="logo_url"
                                    value={formData.logo_url}
                                    onChange={(e) => handleChange("logo_url", e.target.value)}
                                    placeholder="https://example.com/logo.png"
                                />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Address */}
                <Card>
                    <CardHeader>
                        <CardTitle>Address</CardTitle>
                        <CardDescription>Your business location</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="street">Street Address</Label>
                            <Input
                                id="street"
                                value={formData.address.street}
                                onChange={(e) => handleNestedChange("address", "street", e.target.value)}
                                placeholder="123 Main Street"
                            />
                        </div>
                        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                            <div className="space-y-2">
                                <Label htmlFor="city">City</Label>
                                <Input
                                    id="city"
                                    value={formData.address.city}
                                    onChange={(e) => handleNestedChange("address", "city", e.target.value)}
                                    placeholder="City"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="state">State/Region</Label>
                                <Input
                                    id="state"
                                    value={formData.address.state}
                                    onChange={(e) => handleNestedChange("address", "state", e.target.value)}
                                    placeholder="State"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="zip">Postal Code</Label>
                                <Input
                                    id="zip"
                                    value={formData.address.zip}
                                    onChange={(e) => handleNestedChange("address", "zip", e.target.value)}
                                    placeholder="00000"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="country">Country</Label>
                                <Input
                                    id="country"
                                    value={formData.address.country}
                                    onChange={(e) => handleNestedChange("address", "country", e.target.value)}
                                    placeholder="Ghana"
                                />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Contact */}
                <Card>
                    <CardHeader>
                        <CardTitle>Contact Information</CardTitle>
                        <CardDescription>How customers can reach you</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="grid gap-4 md:grid-cols-3">
                            <div className="space-y-2">
                                <Label htmlFor="phone">Phone</Label>
                                <Input
                                    id="phone"
                                    value={formData.contact.phone}
                                    onChange={(e) => handleNestedChange("contact", "phone", e.target.value)}
                                    placeholder="+233 XX XXX XXXX"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="email">Email</Label>
                                <Input
                                    id="email"
                                    type="email"
                                    value={formData.contact.email}
                                    onChange={(e) => handleNestedChange("contact", "email", e.target.value)}
                                    placeholder="info@business.com"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="website">Website</Label>
                                <Input
                                    id="website"
                                    value={formData.contact.website}
                                    onChange={(e) => handleNestedChange("contact", "website", e.target.value)}
                                    placeholder="www.business.com"
                                />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Financial */}
                <Card>
                    <CardHeader>
                        <CardTitle>Financial Settings</CardTitle>
                        <CardDescription>Tax and currency configuration</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="grid gap-4 md:grid-cols-2">
                            <div className="space-y-2">
                                <Label htmlFor="currency">Currency</Label>
                                <Select
                                    value={formData.currency}
                                    onValueChange={(value) => handleChange("currency", value)}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select currency" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="GHS">GHS - Ghanaian Cedi</SelectItem>
                                        <SelectItem value="USD">USD - US Dollar</SelectItem>
                                        <SelectItem value="EUR">EUR - Euro</SelectItem>
                                        <SelectItem value="GBP">GBP - British Pound</SelectItem>
                                        <SelectItem value="NGN">NGN - Nigerian Naira</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="tax_rate">Default Tax Rate (%)</Label>
                                <Input
                                    id="tax_rate"
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    max="100"
                                    value={formData.tax_rate}
                                    onChange={(e) => handleChange("tax_rate", parseFloat(e.target.value) || 0)}
                                />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Inventory Settings */}
                <Card>
                    <CardHeader>
                        <CardTitle>Inventory Settings</CardTitle>
                        <CardDescription>Configure stock management behavior</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="flex items-start justify-between gap-4 p-4 rounded-lg border bg-muted/30">
                            <div className="flex-1 space-y-1">
                                <div className="flex items-center gap-2">
                                    <Label htmlFor="allow_negative_stock" className="text-base font-medium cursor-pointer">
                                        Allow Negative Stock
                                    </Label>
                                    <AlertTriangle className="h-4 w-4 text-orange-500" />
                                </div>
                                <p className="text-sm text-muted-foreground">
                                    When enabled, users can sell products even when out of stock. Stock quantities will go negative and can be adjusted later. Useful for scenarios where stock adjustments happen after sales.
                                </p>
                                {formData.allow_negative_stock && (
                                    <p className="text-sm text-orange-600 dark:text-orange-400 font-medium flex items-center gap-1 mt-2">
                                        <AlertTriangle className="h-3 w-3" />
                                        Warning: This allows selling unavailable products
                                    </p>
                                )}
                            </div>
                            <Switch
                                id="allow_negative_stock"
                                checked={formData.allow_negative_stock}
                                onCheckedChange={(checked) => handleChange("allow_negative_stock", checked)}
                            />
                        </div>
                    </CardContent>
                </Card>

                {/* Media Integrations */}
                <Card>
                    <CardHeader>
                        <CardTitle>Media Integrations</CardTitle>
                        <CardDescription>Configure ImageKit for product media uploads (Optional)</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="grid gap-4 md:grid-cols-2">
                            <div className="space-y-2">
                                <Label htmlFor="imagekit_public_key">Public Key</Label>
                                <Input
                                    id="imagekit_public_key"
                                    value={formData.imagekit_public_key}
                                    onChange={(e) => handleChange("imagekit_public_key", e.target.value)}
                                    placeholder="public_..."
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="imagekit_private_key">Private Key</Label>
                                <Input
                                    id="imagekit_private_key"
                                    type="password"
                                    value={formData.imagekit_private_key}
                                    onChange={(e) => handleChange("imagekit_private_key", e.target.value)}
                                    placeholder="private_..."
                                />
                            </div>
                            <div className="space-y-2 md:col-span-2">
                                <Label htmlFor="imagekit_url_endpoint">URL Endpoint</Label>
                                <Input
                                    id="imagekit_url_endpoint"
                                    value={formData.imagekit_url_endpoint}
                                    onChange={(e) => handleChange("imagekit_url_endpoint", e.target.value)}
                                    placeholder="https://ik.imagekit.io/your_id"
                                />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Receipt Settings */}
                <Card>
                    <CardHeader>
                        <CardTitle>Receipt Settings</CardTitle>
                        <CardDescription>Customize your receipt appearance</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="space-y-2">
                            <Label>Printer Type</Label>
                            <div className="grid grid-cols-2 gap-4">
                                <button
                                    type="button"
                                    onClick={() => handleChange("printer_type", "normal")}
                                    className={`flex flex-col items-center gap-3 p-4 rounded-lg border-2 transition-all ${
                                        formData.printer_type === "normal"
                                            ? "border-primary bg-primary/5"
                                            : "border-muted hover:border-muted-foreground/30"
                                    }`}
                                >
                                    <Monitor className={`h-8 w-8 ${formData.printer_type === "normal" ? "text-primary" : "text-muted-foreground"}`} />
                                    <div className="text-center">
                                        <p className="font-medium text-sm">Normal Printer</p>
                                        <p className="text-xs text-muted-foreground">A4 / Letter size receipts</p>
                                    </div>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => handleChange("printer_type", "terminal")}
                                    className={`flex flex-col items-center gap-3 p-4 rounded-lg border-2 transition-all ${
                                        formData.printer_type === "terminal"
                                            ? "border-primary bg-primary/5"
                                            : "border-muted hover:border-muted-foreground/30"
                                    }`}
                                >
                                    <Printer className={`h-8 w-8 ${formData.printer_type === "terminal" ? "text-primary" : "text-muted-foreground"}`} />
                                    <div className="text-center">
                                        <p className="font-medium text-sm">Terminal Printer</p>
                                        <p className="text-xs text-muted-foreground">Thermal / POS receipt printer</p>
                                    </div>
                                </button>
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="receipt_notes">Receipt Notes / Thank You Message</Label>
                            <Textarea
                                id="receipt_notes"
                                value={formData.receipt_notes}
                                onChange={(e) => handleChange("receipt_notes", e.target.value)}
                                placeholder="Thank you for your business!"
                                rows={3}
                            />
                        </div>
                    </CardContent>
                </Card>

                {/* Save Button */}
                <div className="flex justify-end">
                    <Button type="submit" size="lg" disabled={isLoading}>
                        {isLoading ? (
                            <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Saving...
                            </>
                        ) : (
                            <>
                                <Save className="mr-2 h-4 w-4" />
                                Save Changes
                            </>
                        )}
                    </Button>
                </div>
            </form>
        </div>
    );
}
